import { createElement } from 'react';
import {
  PiCodeFill,
  PiFactoryFill,
  PiGearFill,
  PiHouseFill,
  PiMoneyFill,
  PiTruckFill,
  PiUsersFill,
  PiWrenchFill,
} from 'react-icons/pi';
import { IoPieChart } from 'react-icons/io5';

import {
  ANALYSIS_READ_CODES,
  AFTERSALES_REPAIR_ITEM_READ_CODES,
  AFTERSALES_REPAIR_REPORT_READ_CODES,
  AFTERSALES_REPAIR_REQUEST_READ_CODES,
  AFTERSALES_SIGN_RECEIPT_READ_CODES,
  AFTERSALES_SN_PRODUCT_MAPPING_READ_CODES,
  BOM_READ_CODES,
  CUSTOMER_ORDER_READ_CODES,
  CUSTOMER_READ_CODES,
  DOCUMENT_MGMT_READ_CODES,
  FMS_PAYMENT_REQUEST_READ_CODES,
  FMS_VOUCHER_READ_CODES,
  HOME_READ_CODES,
  INVENTORY_STOCK_READ_CODES,
  MATERIAL_MGMT_READ_CODES,
  MISC_PURCHASE_ORDER_READ_CODES,
  PERM,
  PROCESSING_FACTORY_READ_CODES,
  PRODUCT_READ_CODES,
  PURCHASE_ORDER_READ_CODES,
  QUOTATION_READ_CODES,
  COST_ESTIMATE_READ_CODES,
  SALES_ORDER_READ_CODES,
  STORE_PLACE_READ_CODES,
  SUPPLIER_READ_CODES,
  WORK_ORDER_READ_CODES,
} from '#/config/permissions';
import { ROUTE_BASE, ROUTES } from '#/config/routes';

/**
 * Aside 選單資料 — 結構對齊 v1 asideMenuList，以 enabled 控制占位／上線。
 * SP-09 擴充 mtrStruct 等連結時只增子項，不改整體 shape。
 * 可導覽項掛 permissionAny 群組常數；空陣列＝不過濾（見 filterAsideMenuByPermissions）。
 */
const baseAsideMenuList = [
  {
    to: ROUTES.home,
    icon: createElement(IoPieChart, { size: 20 }),
    title: '主控台',
    enabled: true,
    permissionAny: HOME_READ_CODES,
  },
  {
    to: ROUTES.warehouse,
    icon: createElement(PiHouseFill, { size: 20 }),
    title: '倉儲',
    enabled: true,
    children: [
      {
        to: ROUTES.inventoryStock,
        title: '庫存',
        enabled: true,
        permissionAny: INVENTORY_STOCK_READ_CODES,
      },
      {
        to: ROUTES.inventorySafetyCheck,
        title: '安全庫存',
        enabled: true,
        permissionAny: INVENTORY_STOCK_READ_CODES,
      },
      {
        to: ROUTES.materialMgmt,
        title: '物料管理',
        enabled: true,
        /** 任一子功能 read 即可顯示群組（範本：permissionAny） */
        permissionAny: MATERIAL_MGMT_READ_CODES,
      },
      {
        to: ROUTES.documentMgmt,
        title: '單據管理',
        enabled: true,
        permissionAny: DOCUMENT_MGMT_READ_CODES,
        /** 入庫等單據列表不在 document-mgmt 路徑下，仍須高亮本項 */
        matchPrefixes: [
          ROUTES.stockReceipt,
          ROUTES.stockTransfer,
          ROUTES.stockIssue,
          ROUTES.stockAdjustment,
          ROUTES.stockIssueCategory,
          ROUTES.stocktake,
        ],
      },
      {
        to: ROUTES.storePlaceMgmt,
        title: '倉別管理',
        enabled: true,
        permissionAny: STORE_PLACE_READ_CODES,
      },
    ],
  },
  {
    to: ROUTES.production,
    icon: createElement(PiFactoryFill, { size: 20 }),
    title: '生產製造',
    enabled: true,
    children: [
      {
        to: ROUTES.bom,
        title: 'BOM列表',
        enabled: true,
        permissionAny: BOM_READ_CODES,
      },
      {
        to: ROUTES.workOrder,
        title: '製造命令',
        enabled: true,
        permissionAny: WORK_ORDER_READ_CODES,
      },
      {
        to: ROUTES.analysis,
        title: '分析',
        enabled: true,
        permissionAny: ANALYSIS_READ_CODES,
      },
      {
        to: ROUTES.processingFactory,
        title: '加工廠',
        enabled: true,
        permissionAny: PROCESSING_FACTORY_READ_CODES,
      },
    ],
  },
  {
    to: `${ROUTE_BASE}/crm-mgmt`,
    icon: createElement(PiUsersFill, { size: 20 }),
    title: '客戶管理',
    enabled: true,
    children: [
      {
        to: ROUTES.customerOrder,
        title: '客戶訂單',
        enabled: true,
        permissionAny: CUSTOMER_ORDER_READ_CODES,
      },
      {
        to: ROUTES.quotation,
        title: '報價單',
        enabled: true,
        permissionAny: QUOTATION_READ_CODES,
      },
      {
        to: ROUTES.costEstimate,
        title: '成本試算',
        enabled: true,
        permissionAny: COST_ESTIMATE_READ_CODES,
      },
      {
        to: ROUTES.salesOrder,
        title: '銷貨單',
        enabled: true,
        permissionAny: SALES_ORDER_READ_CODES,
      },
      {
        to: ROUTES.snSalesWarranty,
        title: '序號直接銷售',
        enabled: true,
        permissionAny: SALES_ORDER_READ_CODES,
      },
      {
        to: ROUTES.product,
        title: '產品管理',
        enabled: true,
        permissionAny: PRODUCT_READ_CODES,
      },
      {
        to: ROUTES.customer,
        title: '客戶管理',
        enabled: true,
        permissionAny: CUSTOMER_READ_CODES,
      },
    ],
  },
  {
    to: `${ROUTE_BASE}/product-repair`,
    icon: createElement(PiWrenchFill, { size: 20 }),
    title: '產品維修',
    enabled: true,
    children: [
      {
        to: ROUTES.repairRequest,
        title: '報修單',
        enabled: true,
        permissionAny: AFTERSALES_REPAIR_REQUEST_READ_CODES,
      },
      {
        to: ROUTES.repairItem,
        title: '維修品項',
        enabled: true,
        permissionAny: AFTERSALES_REPAIR_ITEM_READ_CODES,
      },
      {
        to: ROUTES.repairReport,
        title: '維修報告',
        enabled: true,
        permissionAny: AFTERSALES_REPAIR_REPORT_READ_CODES,
      },
      {
        to: ROUTES.signReceipt,
        title: '簽收單',
        enabled: true,
        permissionAny: AFTERSALES_SIGN_RECEIPT_READ_CODES,
      },
      {
        to: ROUTES.snProductMapping,
        title: '物料序號',
        enabled: true,
        permissionAny: AFTERSALES_SN_PRODUCT_MAPPING_READ_CODES,
      },
    ],
  },
  {
    to: ROUTES.srmMgmt,
    icon: createElement(PiTruckFill, { size: 20 }),
    title: '供應商管理',
    enabled: true,
    children: [
      {
        to: ROUTES.purchaseOrder,
        title: '物料採購單',
        enabled: true,
        permissionAny: [
          ...PURCHASE_ORDER_READ_CODES,
          ...MISC_PURCHASE_ORDER_READ_CODES,
        ],
      },
      {
        to: ROUTES.supplier,
        title: '供應商管理',
        enabled: true,
        permissionAny: SUPPLIER_READ_CODES,
      },
    ],
  },
  {
    to: ROUTES.fmsMgmt,
    icon: createElement(PiMoneyFill, { size: 20 }),
    title: '財務管理',
    enabled: true,
    children: [
      {
        to: ROUTES.fmsPaymentRequest,
        title: '請款單',
        enabled: true,
        permissionAny: FMS_PAYMENT_REQUEST_READ_CODES,
      },
      {
        to: ROUTES.fmsVoucher,
        title: '憑證',
        enabled: true,
        permissionAny: FMS_VOUCHER_READ_CODES,
      },
    ],
  },
  {
    to: ROUTES.systemSetting,
    icon: createElement(PiGearFill, { size: 20 }),
    title: '系統設定',
    enabled: true,
    children: [
      {
        to: ROUTES.systemUsers,
        title: '帳號管理',
        enabled: true,
        permission: PERM.SYS_ACCOUNT_READ,
      },
      {
        to: ROUTES.systemRoles,
        title: '職務管理',
        enabled: true,
        permission: PERM.SYS_ROLE_READ,
      },
      {
        to: ROUTES.systemUserAccess,
        title: '使用者存取',
        enabled: true,
        permissionAll: [PERM.SYS_ACCOUNT_READ, PERM.SYS_ROLE_READ],
      },
      {
        to: `${ROUTE_BASE}/setting/groups-list`,
        title: '權限管理',
        enabled: false,
      },
    ],
  },
];

/** 開發工具分類：僅 dev 出現（build 後 import.meta.env.DEV 為 false）。 */
const devToolsAsideItem = {
  to: ROUTES.devTools,
  icon: createElement(PiCodeFill, { size: 20 }),
  title: '開發工具',
  enabled: true,
  children: [
    { to: ROUTES.layoutDemo, title: 'PageShell 四變體', enabled: true },
    { to: ROUTES.tableDemo, title: '表格示範', enabled: true },
    { to: ROUTES.dialogDemo, title: 'Dialog 示範', enabled: true },
    { to: ROUTES.stockReceiptPreview, title: '入庫明細預覽', enabled: true },
    { to: ROUTES.errorTest, title: 'Error 測試', enabled: true },
  ],
};

export function createAsideMenuList({ isDev = false } = {}) {
  return isDev ? [...baseAsideMenuList, devToolsAsideItem] : baseAsideMenuList;
}

const asideMenuList = createAsideMenuList({ isDev: import.meta.env.DEV });

export default asideMenuList;
